import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@staff-app/ui';
import {
  INTL_DEVICE_LABELS,
  INTL_REQUEST_MIN_NOTE_LEN,
  formatMinor,
  intlRequestNo,
  type IntlRequest,
} from '@staff-app/shared-types';
import { countryName, useSupersedeIntlQuote } from '../hooks/useIntlRequests';

/**
 * RE-PRICE A QUOTE — cancel the standing one and raise a replacement.
 *
 * WHY THIS IS A DIALOG AND CANCELLING IS NOT. Cancelling withdraws an offer and is one click,
 * because there is nothing to decide. Superseding REPLACES an offer the customer may already be
 * holding, and it asks for two things a button cannot: the new figures, and why.
 *
 * ⚠️ THE REASON IS REQUIRED, and it is not paperwork. It is written into the request's notes
 * beside the superseded quote's id and figures, and it is the only record of why the price
 * changed — the row now holds the NEW numbers and the Stripe quote holds no explanation at all.
 * "Why did we quote them 12% less in October" is asked long after everyone has forgotten.
 *
 * ⚠️ THERE IS NO PRICE FIELD, AND THAT IS THE POINT (requester, 2026-09-21). A superseding quote
 * is re-priced from the CATALOG. Re-quoting exists for the case where the request moved
 * underneath the quote — the quantity was corrected, the destination changed — and the honest
 * answer to that is today's catalog rather than a figure somebody retypes beside it.
 *
 * FREIGHT WENT WITH IT for a mechanical reason rather than a policy one: the server only honours
 * a typed freight total when a unit price came with it, so a freight box here would accept a
 * number that could never reach the quote. A field that is silently ignored is worse than no
 * field — the rep believes they set it.
 *
 * SO WHAT IF THE PRICE ITSELF IS BEING RENEGOTIATED? Cancel the quote and raise a new one. That
 * is a deliberate two-step: replacing an offer at a number nobody can trace back to the catalog
 * should not be the same click as correcting a quantity. The server refuses a typed price here
 * too, so this is explained by the dialog and enforced somewhere a direct API call also meets it.
 */
export function SupersedeQuoteDialog({
  request,
  open,
  onOpenChange,
}: {
  request: IntlRequest;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [days, setDays] = useState('');
  const [reason, setReason] = useState('');

  const [supersede, { loading, error }] = useSupersedeIntlQuote();

  const daysOk = !days.trim() || /^\d{1,3}$/.test(days.trim());
  const reasonOk = reason.trim().length >= INTL_REQUEST_MIN_NOTE_LEN;
  const canSave = daysOk && reasonOk && !loading;

  const submit = async () => {
    if (!canSave) return;
    try {
      const res = await supersede({
        variables: {
          input: {
            // No `unitAmount` / `shippingAmount`: the replacement is priced from the catalog, and
            // the server refuses them outright rather than ignoring them.
            requestId: request.id,
            ...(days.trim() ? { expiresInDays: Number(days.trim()) } : {}),
            supersedeReason: reason.trim(),
          },
        },
      });
      if (res.data?.supersedeIntlQuote) onOpenChange(false);
    } catch {
      // Rendered from the hook's `error` — Apollo throws AND populates it.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="intl-supersede-dialog">
        <DialogHeader>
          <DialogTitle>{`Re-price ${intlRequestNo(request.id)}`}</DialogTitle>
          <DialogDescription>
            {`${request.quantity} × ${INTL_DEVICE_LABELS[request.deviceModel] ?? request.deviceModel}`}
            {` to ${countryName(request.shipToCountry)}.`}
            {request.quoteTotal != null
              ? ` Currently quoted at ${formatMinor(
                  request.quoteTotal,
                  request.quoteCurrency ?? 'usd',
                )}.`
              : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* WHAT IT WILL COST, not a box to type one into. The figure comes from the catalog at
              the moment the replacement is raised, so the honest thing to show here is where it
              comes from rather than a number that might be stale by the time it is pressed. */}
          <p className="text-sm text-muted-foreground" data-testid="intl-supersede-repriced">
            {'The replacement is priced from the catalog for '}
            <span className="font-medium text-foreground">
              {`${request.quantity} × ${INTL_DEVICE_LABELS[request.deviceModel] ?? request.deviceModel}`}
            </span>
            {` to ${countryName(request.shipToCountry)}, with freight for that destination.`}
          </p>

          <div>
            <Label className="text-xs font-medium text-muted-foreground">{'Valid for (days)'}</Label>
            <Input
              value={days}
              onChange={(e) => setDays(e.target.value)}
              placeholder="30"
              inputMode="numeric"
              className="mt-1"
              data-testid="intl-supersede-days"
            />
          </div>

          <div>
            <Label className="text-xs font-medium text-muted-foreground">
              {'Why is the price changing?'}
            </Label>
            {/* An `Input`, not a textarea: `@staff-app/ui` has none, and the drawer's own note
                field is an Input for the same reason. A reason for a price change is a sentence. */}
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Customer increased the order to 1,200 units and asked for the volume rate."
              className="mt-1"
              data-testid="intl-supersede-reason"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {'Goes into this request’s notes with the old quote’s figures. It is the only '}
              {'record of why the price moved.'}
            </p>
          </div>

          <p className="text-xs text-muted-foreground">
            {'The current quote is cancelled in Stripe and a new one is raised and finalized. '}
            {'Nothing is billed — the customer still has to agree, and somebody still has to '}
            {'convert it.'}
          </p>

          {error ? (
            <p className="text-sm text-destructive" data-testid="intl-supersede-error">
              {error.message}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {'Cancel'}
          </Button>
          <Button onClick={submit} disabled={!canSave} data-testid="intl-supersede-save">
            {loading ? 'Re-pricing…' : 'Replace the quote'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
